// Build bump: 2026-05-21T17:20Z — force redeploy: aggressive control-bar collapse (desktop only).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { saveVideoProgress, getVideoProgress } from "@/lib/progress.functions";


/**
 * Wraps a <MuxPlayer> with editorial styling.
 *
 * Desktop (>=768px):
 *   - No frame, no background — video sits directly on the page.
 *   - Native Mux controls hidden except for the progress scrubber.
 *   - Custom centre play overlay (outline circle + outline triangle, burgundy).
 *   - Custom fullscreen icon bottom-right (hover-revealed).
 *
 * Mobile (<768px):
 *   - Cream wrapper retained.
 *   - Native fullscreen button retained.
 *   - Visual-only burgundy progress bar overlay (since --time-range is hidden).
 *
 * If `lessonId` is provided, the player saves video position to the server
 * (throttled timeupdate + on pause/visibilitychange) and seeks to the saved
 * position on loadedmetadata. Omitting `lessonId` (e.g. FreePreview) keeps
 * the player anonymous — no server reads or writes.
 */
export function MinimalPlayerFrame({
  playerRef,
  aspectRatio = "16 / 9",
  maxWidth,
  lessonId,
  onEnded: onEndedProp,
  children,
}: {
  playerRef: React.MutableRefObject<any>;
  aspectRatio?: string;
  maxWidth?: string;
  lessonId?: string;
  onEnded?: () => void;
  children: ReactNode;
}) {

  const [pct, setPct] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFsHint, setShowFsHint] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Mobile tap-to-reveal state. Desktop ignores both.
  const [hasStarted, setHasStarted] = useState(false);
  const [mobileControlsVisible, setMobileControlsVisible] = useState(false);
  const mobileHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fsHintTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dotRef = useRef<HTMLDivElement | null>(null);
  const fillRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const wasPlayingRef = useRef(false);

  useEffect(() => {
    let frame = 0;
    let attempts = 0;
    let cleanup: (() => void) | undefined;
    const attach = () => {
      const p = playerRef.current;
      if (!p) {
        if (attempts < 60) {
          attempts += 1;
          frame = requestAnimationFrame(attach);
        }
        return;
      }
    const onTime = () => {
      if (draggingRef.current) return;
      const d = p.duration || 0;
      const c = p.currentTime || 0;
      if (d > 0) setPct(Math.min(100, (c / d) * 100));
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      try {
        onEndedProp?.();
      } catch {}
      // Auto-exit fullscreen when the video finishes so the viewer lands
      // back on the lesson page (and any end-of-video CTA is visible).
      try {
        if (document.fullscreenElement) document.exitFullscreen?.();
        else if ((document as any).webkitFullscreenElement) (document as any).webkitExitFullscreen?.();
      } catch {}
    };

    // Lock volume to max — users use device hardware controls.
    const lockVol = () => {
      try {
        p.volume = 1;
        p.muted = false;
      } catch {}
    };
    lockVol();
    p.addEventListener?.("timeupdate", onTime);
    p.addEventListener?.("play", onPlay);
    p.addEventListener?.("playing", onPlay);
    p.addEventListener?.("pause", onPause);
    p.addEventListener?.("ended", onEnded);
    p.addEventListener?.("volumechange", lockVol);
    // Seed initial state
    try {
      setPlaying(!p.paused);
    } catch {}
    cleanup = () => {
      p.removeEventListener?.("timeupdate", onTime);
      p.removeEventListener?.("play", onPlay);
      p.removeEventListener?.("playing", onPlay);
      p.removeEventListener?.("pause", onPause);
      p.removeEventListener?.("ended", onEnded);
      p.removeEventListener?.("volumechange", lockVol);
    };
    };
    attach();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      cleanup?.();
    };
  }, [playerRef]);

  // Server-backed video position persistence — only when lessonId is provided.
  // Throttled timeupdate (10s), save on pause/visibilitychange/beforeunload,
  // and seek to saved position on loadedmetadata (if reasonable).
  useEffect(() => {
    if (!lessonId) return;
    let cancelled = false;
    let frame = 0;
    let attempts = 0;
    let cleanup: (() => void) | undefined;
    let lastSaveAt = 0;
    let hasSeeked = false;

    const doSave = (sync = false) => {
      const p = playerRef.current;
      if (!p) return;
      const pos = p.currentTime || 0;
      const dur = p.duration || 0;
      if (pos < 1) return;
      lastSaveAt = Date.now();
      saveVideoProgress({
        data: {
          lessonId,
          position: Math.floor(pos),
          duration: dur > 0 ? Math.floor(dur) : undefined,
        },
      }).catch(() => {});
      void sync;
    };

    const attach = () => {
      if (cancelled) return;
      const p = playerRef.current;
      if (!p) {
        if (attempts < 60) {
          attempts += 1;
          frame = requestAnimationFrame(attach);
        }
        return;
      }

      const trySeek = () => {
        if (hasSeeked) return;
        const dur = p.duration || 0;
        if (dur <= 0) return;
        getVideoProgress({ data: { lessonId } })
          .then((res) => {
            if (cancelled || hasSeeked) return;
            hasSeeked = true;
            const pos = res?.position ?? 0;
            // Avoid seeking right back to 0 or skipping past the end.
            if (pos > 5 && pos < dur - 10) {
              try {
                p.currentTime = pos;
              } catch {}
            }
          })
          .catch(() => {});
      };

      const onLoadedMetadata = () => trySeek();
      const onTimeUpdate = () => {
        if (Date.now() - lastSaveAt >= 10_000) doSave();
      };
      const onPause = () => doSave();
      const onVisibility = () => {
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
          doSave();
        }
      };
      const onBeforeUnload = () => doSave(true);

      // If metadata is already loaded by the time we attach (cached player),
      // seek immediately.
      if ((p.duration || 0) > 0) trySeek();

      p.addEventListener?.("loadedmetadata", onLoadedMetadata);
      p.addEventListener?.("timeupdate", onTimeUpdate);
      p.addEventListener?.("pause", onPause);
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibility);
      }
      if (typeof window !== "undefined") {
        window.addEventListener("beforeunload", onBeforeUnload);
      }

      cleanup = () => {
        p.removeEventListener?.("loadedmetadata", onLoadedMetadata);
        p.removeEventListener?.("timeupdate", onTimeUpdate);
        p.removeEventListener?.("pause", onPause);
        if (typeof document !== "undefined") {
          document.removeEventListener("visibilitychange", onVisibility);
        }
        if (typeof window !== "undefined") {
          window.removeEventListener("beforeunload", onBeforeUnload);
        }
        // Final save on unmount (e.g. navigating to next lesson).
        doSave();
      };
    };
    attach();

    return () => {
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
      cleanup?.();
    };
  }, [playerRef, lessonId]);

  // Track fullscreen state (driven by the wrapper element entering/leaving FS)
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onFsChange = () => {
      const fsEl = (document.fullscreenElement || (document as any).webkitFullscreenElement) as Element | null;
      // Defensive: any non-null fullscreen element counts. The wrapper is the
      // only thing we ever fullscreen, so equality is implicit.
      setIsFullscreen(!!fsEl);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange as any);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange as any);
    };
  }, []);

  // Defensive: strip any `loop` attribute from the inner <video> element so
  // the video stops on its final frame instead of restarting.
  useEffect(() => {
    let frame = 0;
    let attempts = 0;
    const strip = () => {
      const p = playerRef.current as HTMLElement | null;
      const video = (p as any)?.media || p?.shadowRoot?.querySelector?.("video");
      if (video) {
        try {
          video.loop = false;
          video.removeAttribute?.("loop");
        } catch {}
        try {
          (p as any).loop = false;
          p?.removeAttribute?.("loop");
        } catch {}
        return;
      }
      if (attempts < 60) {
        attempts += 1;
        frame = requestAnimationFrame(strip);
      }
    };
    strip();
    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [playerRef]);


  useEffect(() => {
    if (typeof document === "undefined") return;

    let frame = 0;
    let attempts = 0;
    let cancelled = false;
    const css = `
      media-control-bar[part~='bottom']::before,
      media-control-bar[part~='top']::before {
        content: none !important;
        display: none !important;
        opacity: 0 !important;
        background: none !important;
        background-image: none !important;
      }
      media-control-bar {
        --gradient-steps: hsl(0 0% 0% / 0) 0%, hsl(0 0% 0% / 0) 100% !important;
        height: 0 !important;
        min-height: 0 !important;
        overflow: hidden !important;
      }
      /* Desktop-only: fully collapse every control bar (top + bottom chrome).
         Mobile (<768px) keeps Mux's native bar available per design memory. */
      @media (min-width: 768px) {
        media-controller media-control-bar,
        media-controller media-control-bar[part~='bottom'],
        media-controller media-control-bar[part~='top'],
        media-controller media-control-bar[slot~='top-chrome'] {
          display: none !important;
          height: 0 !important;
          min-height: 0 !important;
          max-height: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
          overflow: hidden !important;
        }
      }
      media-time-range {
        --media-range-track-background: rgba(139, 30, 30, 0.15) !important;
        --media-range-track-color: rgba(139, 30, 30, 0.15) !important;
        --media-time-range-buffered-color: transparent !important;
        --media-range-bar-color: #8B1E1E !important;
        --media-range-thumb-background: #8B1E1E !important;
        --media-range-thumb-border: 2px solid #F4EFE6 !important;
      }
      /* Top chrome / gradient suppression (in case Mux draws a top fog in FS) */
      media-controller::part(top-chrome),
      media-control-bar[part~='top'] {
        background: transparent !important;
        background-image: none !important;
        box-shadow: none !important;
      }
      /* Issue D: kill focus outlines / rings inside the shadow DOM that
         appear as thin top/right edge lines on hover or click. */
      :host,
      :host(:focus),
      :host(:focus-visible),
      :host(:focus-within) {
        outline: none !important;
        box-shadow: none !important;
      }
      *:focus,
      *:focus-visible,
      *:focus-within {
        outline: none !important;
        box-shadow: none !important;
      }
      video, media-controller, media-control-bar {
        outline: none !important;
        box-shadow: none !important;
      }
      /* Suppress any border on shadow DOM player elements — appears as thin
         grey top/right edge lines during control-bar fade in/out. */
      :host,
      media-controller,
      media-control-bar,
      media-time-range,
      media-play-button,
      media-fullscreen-button,
      video {
        border: none !important;
        border-width: 0 !important;
      }
      /* Suppress caption/text-track rendering inside shadow DOM — these
         draw a 1px baseline at the bottom (or a 1px selectmenu slot on the
         right) on lessons whose Mux asset advertises a text track, even
         when captions are hidden. */
      media-text-display,
      media-captions-selectmenu,
      media-rendition-selectmenu,
      media-audio-track-selectmenu {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        border: 0 !important;
      }
      video::cue { display: none !important; background: transparent !important; }
      video::-webkit-media-text-track-container,
      video::-webkit-media-text-track-display,
      video::-webkit-media-text-track-background {
        display: none !important;
        visibility: hidden !important;
      }
    `;



    const injectStyle = (root: ShadowRoot) => {
      let style = root.querySelector<HTMLStyleElement>("style[data-wk-mux-minimal]");
      if (!style) {
        style = document.createElement("style");
        style.setAttribute("data-wk-mux-minimal", "true");
        root.appendChild(style);
      }
      style.textContent = css;
    };

    const injectMuxShadowStyles = () => {
      const p = playerRef.current as HTMLElement | null;
      if (!p) {
        if (!cancelled && attempts < 60) {
          attempts += 1;
          frame = requestAnimationFrame(injectMuxShadowStyles);
        }
        return;
      }
      const muxRoot = (p as any).shadowRoot as ShadowRoot | null | undefined;
      const themeRoot = ((p as any).mediaTheme?.shadowRoot || muxRoot?.querySelector("media-theme")?.shadowRoot) as
        | ShadowRoot
        | null
        | undefined;
      if (muxRoot && themeRoot) {
        injectStyle(muxRoot);
        injectStyle(themeRoot);
        return;
      }
      if (!cancelled && attempts < 60) {
        attempts += 1;
        frame = requestAnimationFrame(injectMuxShadowStyles);
      }
    };

    injectMuxShadowStyles();
    return () => {
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
    };
  }, [playerRef]);




  const handlePlay = () => {
    const p = playerRef.current;
    setPlaying(true);
    try {
      p?.play?.();
    } catch {}
  };

  const handleFullscreen = () => {
    // If already fullscreen, exit. Otherwise fullscreen the WRAPPER so our
    // overlays remain inside the fullscreen root and stay visible.
    if (isFullscreen) {
      try {
        if (document.exitFullscreen) document.exitFullscreen();
        else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
      } catch {}
      return;
    }
    const el = wrapperRef.current as any;
    try {
      if (el?.requestFullscreen) el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else {
        const p = playerRef.current;
        if (p?.requestFullscreen) p.requestFullscreen();
        else if (p?.webkitRequestFullscreen) p.webkitRequestFullscreen();
      }
    } catch {}
  };

  const triggerFsHint = () => {
    if (!isFullscreen || !playing) return;
    setShowFsHint(true);
    if (fsHintTimeout.current) clearTimeout(fsHintTimeout.current);
    fsHintTimeout.current = setTimeout(() => setShowFsHint(false), 2000);
  };

  useEffect(() => {
    if (!isFullscreen || !playing) setShowFsHint(false);
  }, [isFullscreen, playing]);

  useEffect(() => () => {
    if (fsHintTimeout.current) clearTimeout(fsHintTimeout.current);
  }, []);

  // Mobile: mark hasStarted on first play; manage auto-hide timer.
  useEffect(() => {
    if (playing && !hasStarted) setHasStarted(true);
  }, [playing, hasStarted]);

  useEffect(() => {
    if (mobileHideTimer.current) {
      clearTimeout(mobileHideTimer.current);
      mobileHideTimer.current = null;
    }
    if (mobileControlsVisible && playing) {
      mobileHideTimer.current = setTimeout(() => setMobileControlsVisible(false), 3000);
    }
    return () => {
      if (mobileHideTimer.current) {
        clearTimeout(mobileHideTimer.current);
        mobileHideTimer.current = null;
      }
    };
  }, [mobileControlsVisible, playing]);

  const handleMobileCentreTap = (e: React.MouseEvent) => {
    e.stopPropagation();
    const p = playerRef.current;
    if (playing) {
      // Pause — keep controls visible (paused = sticky, no auto-hide timer).
      try { p?.pause?.(); } catch {}
    } else {
      // Play (initial or resume) — hide controls so video is unobstructed.
      setHasStarted(true);
      try { p?.play?.(); } catch {}
      setMobileControlsVisible(false);
    }
  };

  const handleMobileFsTap = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleFullscreen();
  };

  const handleMobileReceiverTap = () => {
    setMobileControlsVisible((v) => !v);
  };

  return (
    <>
      <style>{`
        /* Shared Mux player styling for both #free-preview and #lesson-player.
           Scoped to .wk-player wrappers so it applies wherever MinimalPlayerFrame mounts. */
        .wk-player mux-player {
          --media-accent-color: #8B1E1E;
          --media-primary-color: #1A1612;
          --media-secondary-color: rgba(244, 239, 230, 0.85);
          --media-background-color: #F4EFE6;
          --media-object-fit: contain;
          --media-range-bar-color: #8B1E1E;
          --media-range-track-color: rgba(26, 22, 18, 0.15);
          --media-range-thumb-background: #8B1E1E;

          /* Defuse Mux's internal focus-visible shadow at the variable level.
             Mux's shadow-DOM rule is :host(:focus-visible) { box-shadow:
             var(--media-focus-box-shadow, inset 0 0 0 2px rgb(27 127 204 / .9)); }.
             That rule fires the moment focus moves into the player (e.g. after
             the user clicks our overlay play button and we call .play()), and
             an external .wk-player mux-player { box-shadow: none } loses on
             specificity to :host(:focus-visible). Setting the variable to
             none defangs it at source — no specificity battle required. */
          --media-focus-box-shadow: none;

          /* Hide everything except time-range by default — at ALL widths.
             We render our own play overlay and fullscreen button on desktop;
             mobile uses our own custom controls + the visual progress bar. */
          --play-button: none;
          --fullscreen-button: none;
          --seek-backward-button: none;
          --seek-forward-button: none;
          --mute-button: none;
          --volume-range: none;
          --captions-button: none;
          --airplay-button: none;
          --pip-button: none;
          --cast-button: none;
          --playback-rate-button: none;
          --time-display: none;
          --duration-display: none;
          --rendition-selectmenu: none;
          --audio-track-selectmenu: none;
          --live-button: none;
          --title-display: none;
          --loading-indicator: none;

          border: 0 !important;
          outline: 0 !important;
          box-shadow: none !important;
        }

        /* Fullscreen: keep the cream background instead of black */
        .wk-player mux-player:fullscreen,
        .wk-player mux-player:-webkit-full-screen {
          background-color: #F4EFE6 !important;
          --media-background-color: #F4EFE6 !important;
        }
        /* When the wrapper itself is fullscreened (so our overlays stay
           inside the fullscreen root and remain visible), fill the screen
           with cream and let the player fill the wrapper. */
        .wk-player:fullscreen,
        .wk-player:-webkit-full-screen {
          width: 100vw !important;
          height: 100vh !important;
          max-width: none !important;
          aspect-ratio: auto !important;
          background-color: #F4EFE6 !important;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: center;
        }
        .wk-player:fullscreen .wk-player-video,
        .wk-player:-webkit-full-screen .wk-player-video {
          flex: 1 1 auto;
          aspect-ratio: auto !important;
          min-height: 0;
        }
        .wk-player:fullscreen mux-player,
        .wk-player:-webkit-full-screen mux-player {
          width: 100% !important;
          height: 100% !important;
        }
        ::backdrop { background-color: #F4EFE6 !important; }

        /* Mobile (<768px): cream background retained, native fullscreen button
           kept so students can expand on phones. Hide interactive time-range
           (a visual-only burgundy bar is overlaid in JSX). */
        @media (max-width: 767px) {
          .wk-player mux-player {
            --time-range: none;
            --media-control-bar-background: transparent;
            background-color: #F4EFE6;
          }
          .wk-player mux-player video {
            object-fit: contain;
            background-color: #F4EFE6;
          }
          .wk-player mux-player::part(media-control-bar) {
            background: transparent !important;
            background-color: transparent !important;
            background-image: none !important;
            box-shadow: none !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
          }
        }

        /* Force GPU layer on the clip container AND the actual painted
           video element to prevent subpixel edge compositor seams
           (top/bottom flicker on scroll). Promoting only the clip parent
           left the inner <video> layer free to re-rasterise on scroll. */
        .wk-player-video {
          transform: translateZ(0);
          contain: paint;
        }
        .wk-player mux-player,
        .wk-player mux-player video {
          transform: translateZ(0);
        }


        /* Desktop (>=768px): no frame, no background, no gradient fog. */
        @media (min-width: 768px) {
          .wk-player mux-player {
            /* Hide Mux's interactive time-range on desktop too — our own
               custom progress bar (rendered below the video) handles seek. */
            --time-range: none;
            /* --play-button / --fullscreen-button suppressed globally above. */


            /* Control bar surfaces — kept transparent as a backstop */
            --media-control-bar-background: transparent;
            --media-control-background: transparent;
            --controls-backdrop-color: transparent;

            /* Progress bar base values. Mux 3.13.0 overrides several of these
               inside its shadow DOM on media-time-range, so the effective
               desktop rules are also applied below via the exported range part. */
            --media-range-bar-color: #8B1E1E;
            --media-range-track-color: rgba(139, 30, 30, 0.15);
            --media-range-track-background: rgba(139, 30, 30, 0.15);
            --media-time-range-buffered-color: transparent;
            --media-range-track-height: 2px;

            /* Scrub thumb: burgundy with paper outline, hidden by default */
            --media-range-thumb-background: #8B1E1E;
            --media-range-thumb-border: 2px solid #F4EFE6;
            --media-range-thumb-width: 12px;
            --media-range-thumb-height: 12px;
            --media-range-thumb-opacity: 0 !important;
            --media-range-thumb-transition: opacity 220ms ease-out, transform 220ms ease-out !important;

            background-color: transparent;
            outline: none !important;
            box-shadow: none !important;
          }
          .wk-player:hover mux-player {
            --media-range-thumb-opacity: 1 !important;
          }
          .wk-player mux-player video {
            object-fit: contain;
            background-color: transparent;
            outline: none !important;
            box-shadow: none !important;
          }

          /* Mux Player 3.13.0 draws the bottom fog with
             media-control-bar[part~='bottom']::before in its shadow DOM.
             The exported part is control-bar (not media-control-bar, and
             there is no gradient-bottom part). Make the gradient variable
             fully transparent, and directly suppress the pseudo-element where
             the browser supports ::part(... )::before. */
          .wk-player mux-player::part(control-bar) {
            --gradient-steps: hsl(0 0% 0% / 0) 0%, hsl(0 0% 0% / 0) 100% !important;
            background: transparent !important;
            background-color: transparent !important;
            background-image: none !important;
            box-shadow: none !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
            padding-bottom: 4px;
            padding-right: 0 !important;
          }
          .wk-player mux-player::part(control-bar)::before {
            content: none !important;
            display: none !important;
            opacity: 0 !important;
            background: none !important;
            background-image: none !important;
          }
          /* Right-edge hairline during playback: collapse the entire
             control-bar to zero height on desktop. We render zero native
             controls here (external scrubber + buttons are siblings),
             so nothing in the bar needs to paint. Scoped to the desktop
             media query — mobile native controls (lines 469-487) untouched. */
          .wk-player mux-player::part(control-bar) {
            height: 0 !important;
            min-height: 0 !important;
            overflow: hidden !important;
          }

          /* Right-edge vertical line: the fullscreen-button slot still
             reserves space even when --fullscreen-button: none removes
             the visual. Fully collapse it. */
          .wk-player mux-player::part(fullscreen-button) {
            display: none !important;
            width: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          /* Suppress caption/text-track slots that draw 1px edge lines on
             lessons whose Mux asset advertises a text track. */
          .wk-player mux-player::part(captions-selectmenu),
          .wk-player mux-player::part(rendition-selectmenu),
          .wk-player mux-player::part(audio-track-selectmenu),
          .wk-player mux-player::part(captions-button) {
            display: none !important;
            width: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .wk-player mux-player video::cue {
            display: none !important;
            background: transparent !important;
          }
          .wk-player mux-player video::-webkit-media-text-track-container,
          .wk-player mux-player video::-webkit-media-text-track-display,
          .wk-player mux-player video::-webkit-media-text-track-background {
            display: none !important;
            visibility: hidden !important;
          }

          /* Mux Player 3.13.0 exports the media-time-range host as part="range".
             Its internal #track background uses --media-range-track-background
             and buffered fill uses --media-time-range-buffered-color. Apply
             vars on the exported range host so they beat Mux's internal
             media-time-range selector while preserving click-to-scrub. */
          .wk-player mux-player::part(range) {
            background: transparent !important;
            background-image: none !important;
            --media-range-track-background: rgba(139, 30, 30, 0.15) !important;
            --media-range-track-color: rgba(139, 30, 30, 0.15) !important;
            --media-time-range-buffered-color: transparent !important;
            --media-range-bar-color: #8B1E1E !important;
            --media-range-track-height: 2px !important;
            --media-range-thumb-background: #8B1E1E !important;
            --media-range-thumb-border: 2px solid #F4EFE6 !important;
            --media-range-thumb-transform: scale(0) !important;
            --media-range-thumb-opacity: 0 !important;
            --media-range-thumb-transition: opacity 220ms ease-out, transform 220ms ease-out !important;
            outline: none !important;
            box-shadow: none !important;
          }
          .wk-player:hover mux-player::part(range) {
            --media-range-thumb-transform: scale(1) !important;
            --media-range-thumb-opacity: 1 !important;
          }
        }

        /* Custom fullscreen icon: hover-revealed on desktop only */
        .wk-fs-btn {
          opacity: 0;
          transition: opacity 220ms ease-out, transform 220ms ease-out;
        }
        .wk-player:hover .wk-fs-btn { opacity: 0.7; }
        .wk-fs-btn:hover { opacity: 1 !important; transform: scale(1.08); }
        .wk-player:fullscreen .wk-fs-btn,
        .wk-player:-webkit-full-screen .wk-fs-btn {
          bottom: 26px !important;
        }

        /* Belt-and-braces: ensure the mobile-only fullscreen button is
           never rendered on desktop, regardless of Tailwind class ordering.
           This prevents a stray burgundy SVG appearing as a thin right-edge
           artefact during playback. */
        @media (min-width: 768px) {
          .wk-fs-btn-mobile { display: none !important; }
        }

        /* Custom external progress bar — hover dot reveal on desktop only. */
        .wk-progress-dot {
          opacity: 0;
          transition: opacity 250ms ease-out;
        }
        @media (min-width: 768px) {
          .wk-player:hover .wk-progress-dot { opacity: 1; }
        }
        @media (max-width: 767px) {
          .wk-progress-dot[data-mobile-visible="true"] { opacity: 1; }
          /* Mobile: bigger visible dot + larger touch target.
             iOS HIG min 44px hit area; 42px tall hit zone feels plenty
             without looking chunky next to a 2px track. */
          .wk-progress-hit {
            padding-top: 20px !important;
            padding-bottom: 20px !important;
            margin-top: -16px !important;
          }
          .wk-progress-dot {
            width: 16px !important;
            height: 16px !important;
          }
        }


        /* Issue D: kill any browser-native focus outlines or box-shadow rings
           that show up as thin top/right edge lines on hover/focus. Scoped
           strictly to the player wrapper — buttons/links elsewhere keep
           their keyboard focus indicators. */
        .wk-player,
        .wk-player *,
        .wk-player mux-player,
        .wk-player video,
        .wk-player :focus,
        .wk-player :focus-visible,
        .wk-player :focus-within {
          outline: none !important;
          box-shadow: none !important;
        }
        /* Belt-and-braces: suppress borders on the player wrapper and any
           descendant (including the mux-player host element). The custom
           progress bar is rendered as a sibling outside .wk-player-video
           and uses background, not border, so this is safe. */
        .wk-player,
        .wk-player mux-player,
        .wk-player video {
          border: none !important;
          border-width: 0 !important;
        }
        .wk-player button {
          border: none !important;
          background-color: transparent;
          -webkit-appearance: none;
          appearance: none;
        }
      `}</style>

      <div
        ref={wrapperRef}
        className="wk-player relative w-full flex flex-col bg-paper md:bg-transparent"
        style={maxWidth ? { maxWidth } : undefined}
      >
        <div
          onMouseMove={triggerFsHint}
          onMouseEnter={() => { setHovered(true); triggerFsHint(); }}
          onMouseLeave={() => setHovered(false)}
          onClick={(e) => {
            // Desktop click-to-pause anywhere on the video area. The
            // progress bar is now a sibling outside this div, so it can't
            // be hit by accident. Skip when paused (centre play handles it).
            if (!playing) return;
            if (typeof window !== "undefined" && window.innerWidth < 768) return;
            const target = e.target as HTMLElement;
            if (target.closest(".wk-fs-btn")) return;
            const p = playerRef.current;
            setPlaying(false);
            try {
              p?.pause?.();
            } catch {}
          }}
          className="wk-player-video relative w-full overflow-hidden"
          style={{ aspectRatio }}
        >
          {children}

          {/* DESKTOP centre play overlay (paused state). */}
          {!playing && (
            <button
              type="button"
              onClick={handlePlay}
              aria-label="Play video"
              className="hidden md:flex absolute inset-0 items-center justify-center bg-transparent border-0 p-0 m-0 cursor-pointer"
              style={{
                outline: "none",
                border: "none",
                boxShadow: "none",
                background: "transparent",
                WebkitAppearance: "none",
                appearance: "none",
                zIndex: 6,
              }}
            >
              <svg
                width="92"
                height="92"
                viewBox="0 0 92 92"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle cx="46" cy="46" r="43" stroke="#8B1E1E" strokeWidth="2.5" fill="none" />
                <path
                  d="M38 30 L66 46 L38 62 Z"
                  stroke="#8B1E1E"
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          )}

          {/* MOBILE tap-receiver */}
          {hasStarted && (
            <div
              aria-hidden="true"
              onClick={handleMobileReceiverTap}
              className="md:hidden absolute inset-0 cursor-pointer"
              style={{ zIndex: 5, background: "transparent" }}
            />
          )}

          {/* MOBILE centre play/pause button (always mounted; opacity-driven). */}
          <button
            type="button"
            onClick={handleMobileCentreTap}
            aria-label={playing ? "Pause video" : "Play video"}
            className="flex md:hidden absolute items-center justify-center bg-transparent border-0 p-0 m-0 cursor-pointer"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 92,
              height: 92,
              outline: "none",
              zIndex: 7,
              opacity: !hasStarted || mobileControlsVisible ? 1 : 0,
              transition: "opacity 350ms ease-out",
              pointerEvents: !hasStarted || mobileControlsVisible ? "auto" : "none",
            }}
          >
            <svg
              width="92"
              height="92"
              viewBox="0 0 92 92"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="46" cy="46" r="43" stroke="#8B1E1E" strokeWidth="2.5" fill="none" />
              {playing ? (
                <>
                  <line x1="38" y1="30" x2="38" y2="62" stroke="#8B1E1E" strokeWidth="2.5" strokeLinecap="round" />
                  <line x1="54" y1="30" x2="54" y2="62" stroke="#8B1E1E" strokeWidth="2.5" strokeLinecap="round" />
                </>
              ) : (
                <path
                  d="M38 30 L66 46 L38 62 Z"
                  stroke="#8B1E1E"
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                  fill="none"
                />
              )}
            </svg>
          </button>

          {/* MOBILE fullscreen button (always mounted; opacity-driven). */}
          <button
            type="button"
            onClick={handleMobileFsTap}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            className="wk-fs-btn-mobile flex md:hidden absolute items-center justify-center bg-transparent border-0 p-0 cursor-pointer"
            style={{
              right: 12,
              bottom: 12,
              width: 22,
              height: 22,
              zIndex: 7,
              opacity: hasStarted && mobileControlsVisible ? 1 : 0,
              transition: "opacity 350ms ease-out",
              pointerEvents: hasStarted && mobileControlsVisible ? "auto" : "none",
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d={isFullscreen
                  ? "M8 3 V8 H3 M14 3 V8 H19 M19 14 H14 V19 M3 14 H8 V19"
                  : "M3 8 V3 H8 M14 3 H19 V8 M19 14 V19 H14 M8 19 H3 V14"}
                stroke="#8B1E1E"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </button>

          {/* Fullscreen-only pause hint */}
          {isFullscreen && playing && (
            <div
              aria-hidden="true"
              className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none"
              style={{
                opacity: showFsHint ? 1 : 0,
                transition: "opacity 220ms ease-out",
                zIndex: 6,
              }}
            >
              <svg
                width="92"
                height="92"
                viewBox="0 0 92 92"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="46" cy="46" r="43" stroke="#8B1E1E" strokeWidth="2.5" fill="none" />
                <line x1="38" y1="30" x2="38" y2="62" stroke="#8B1E1E" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="54" y1="30" x2="54" y2="62" stroke="#8B1E1E" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
          )}

          {/* Inline (non-fullscreen) hover pause hint */}
          {!isFullscreen && playing && (
            <div
              aria-hidden="true"
              className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none"
              style={{
                opacity: hovered ? 1 : 0,
                transition: "opacity 220ms ease-out",
                zIndex: 6,
              }}
            >
              <svg
                width="92"
                height="92"
                viewBox="0 0 92 92"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="46" cy="46" r="43" stroke="#8B1E1E" strokeWidth="2.5" fill="none" />
                <line x1="38" y1="30" x2="38" y2="62" stroke="#8B1E1E" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="54" y1="30" x2="54" y2="62" stroke="#8B1E1E" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
          )}

          {/* Desktop-only custom fullscreen button (hover-revealed) */}
          {(() => {
            const expandPath = "M8 3 V8 H3 M14 3 V8 H19 M19 14 H14 V19 M3 14 H8 V19";
            const minimisePath = "M3 8 V3 H8 M14 3 H19 V8 M19 14 V19 H14 M8 19 H3 V14";
            const activePath = isFullscreen ? expandPath : minimisePath;
            return (
              <button
                type="button"
                onClick={handleFullscreen}
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                className="wk-fs-btn hidden md:flex absolute items-center justify-center bg-transparent border-0 p-0 cursor-pointer"
                style={{ right: 12, bottom: 12, width: 22, height: 22, zIndex: 7 }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d={activePath}
                    stroke="#8B1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
            );
          })()}
        </div>

        {/* Interactive progress bar — sibling BELOW the video on both
            viewports. ~26px tall hit zone (12px padding above/below the
            visible 2px track) for easy clicking + dragging.
            Drag is deferred: during pointermove we only mutate DOM styles
            on the dot + fill (no React re-render, no video.currentTime).
            On pointerup we commit the final seek. */}
        <div
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          onPointerDown={(e) => {
            const p = playerRef.current;
            const track = trackRef.current;
            if (!p || !track) return;
            (e.currentTarget as HTMLDivElement).setPointerCapture?.(e.pointerId);
            draggingRef.current = true;
            wasPlayingRef.current = !p.paused;
            try { if (wasPlayingRef.current) p.pause(); } catch {}
            const rect = track.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const pctNow = ratio * 100;
            if (dotRef.current) dotRef.current.style.left = `${pctNow}%`;
            if (fillRef.current) {
              fillRef.current.style.transition = "none";
              fillRef.current.style.width = `${pctNow}%`;
            }
          }}
          onPointerMove={(e) => {
            if (!draggingRef.current) return;
            const track = trackRef.current;
            if (!track) return;
            const rect = track.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const pctNow = ratio * 100;
            if (dotRef.current) dotRef.current.style.left = `${pctNow}%`;
            if (fillRef.current) fillRef.current.style.width = `${pctNow}%`;
          }}
          onPointerUp={(e) => {
            if (!draggingRef.current) return;
            const p = playerRef.current;
            const track = trackRef.current;
            draggingRef.current = false;
            try { (e.currentTarget as HTMLDivElement).releasePointerCapture?.(e.pointerId); } catch {}
            if (!p || !track) return;
            const d = p.duration || 0;
            const rect = track.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const pctNow = ratio * 100;
            setPct(pctNow);
            if (fillRef.current) fillRef.current.style.transition = "width 200ms linear";
            if (d) { try { p.currentTime = ratio * d; } catch {} }
            if (wasPlayingRef.current) { try { p.play(); } catch {} }
          }}
          onPointerCancel={() => {
            draggingRef.current = false;
            if (fillRef.current) fillRef.current.style.transition = "width 200ms linear";
          }}
          className="wk-progress-hit w-full cursor-pointer"
          style={{
            marginTop: 8 - 16,
            paddingTop: 16,
            paddingBottom: 16,
            touchAction: "none",
          }}
        >
          <div
            ref={trackRef}
            style={{
              position: "relative",
              height: 2,
              background: "rgba(139, 30, 30, 0.15)",
            }}
          >
            <div
              ref={fillRef}
              style={{
                height: "100%",
                width: `${pct}%`,
                background: "#8B1E1E",
                transition: "width 200ms linear",
              }}
            />
            <div
              ref={dotRef}
              className="wk-progress-dot"
              data-mobile-visible={mobileControlsVisible ? "true" : "false"}
              style={{
                position: "absolute",
                left: `${pct}%`,
                top: "50%",
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: "#8B1E1E",
                border: "2px solid #F4EFE6",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
